import React, { useCallback } from 'react';
import { Joyride, STATUS } from 'react-joyride';

// ── Step content helpers ─────────────────────────────────────────────────────

const LegendBarContent = (
  <div style={{ fontSize: 13 }}>
    <p style={{ marginBottom: 6 }}>
      This bar is your <strong>legend</strong> — it shows the shapes and symbols used throughout the diagram.
    </p>
    <p style={{ marginBottom: 0, color: '#555' }}>
      On the left: DFD <strong>element types</strong> (External Entity, Process, Data Store, Data Flow, Trust Boundary).
      On the right: <strong>STRIDE categories</strong> and <strong>metric icons</strong> used on threat cards.
    </p>
  </div>
);

const DfdRegionContent = (
  <div style={{ fontSize: 13 }}>
    <p style={{ marginBottom: 0 }}>
      This is the <strong>DFD canvas</strong> — it shows the Data Flow Diagram of your target application.
      Everything you interact with lives here.
    </p>
  </div>
);

const HoverClickContent = (
  <>
    <style>{`
      @keyframes dfd-interact-demo {
        0%,12%  { border-color: #ccc; background: #fafafa; box-shadow: none; }
        20%,42% { border-color: #1976d2; background: #e3f2fd; box-shadow: 0 0 0 3px #bbdefb; }
        52%,74% { border-color: #e65100; background: #fff3e0; box-shadow: 0 0 0 3px #ffcc80; }
        84%,100%{ border-color: #ccc; background: #fafafa; box-shadow: none; }
      }
    `}</style>
    <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 6px' }}>
      <div style={{
        width: 110, height: 42, border: '2px solid #ccc', borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 600, color: '#444',
        animation: 'dfd-interact-demo 3.8s ease-in-out infinite',
      }}>Module</div>
    </div>
    <p style={{ textAlign: 'center', fontSize: 11, color: '#777', marginBottom: 12 }}>
      hover → <strong style={{ color: '#1976d2' }}>blue</strong>
      &nbsp;&nbsp;·&nbsp;&nbsp;
      click → <strong style={{ color: '#e65100' }}>orange</strong>
    </p>
    <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12.5, lineHeight: 1.75, textAlign: 'left' }}>
      <li><strong>Hover</strong> any element — highlights in blue</li>
      <li><strong>Click</strong> to select — turns orange, sidebar updates</li>
      <li><strong>Double-click</strong> — focus mode, dims everything else</li>
      <li><strong>Click empty space</strong> — deselects, shows all elements</li>
    </ul>
  </>
);

const SidebarContent = (
  <div style={{ fontSize: 12.5 }}>
    <p style={{ marginBottom: 8 }}>After selecting an element, the sidebar loads its data. Use the tab toggle to switch views:</p>
    <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: '1px solid #ddd', marginBottom: 12 }}>
      <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#5e35b1', color: '#fff', fontSize: 12, fontWeight: 700 }}>⚠ Threats</div>
      <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#1565c0', color: '#fff', fontSize: 12, fontWeight: 700 }}>🔗 Paths</div>
    </div>
    <p style={{ marginBottom: 0, color: '#555' }}>
      When nothing is selected the sidebar shows <em>all</em> threats and paths.
    </p>
  </div>
);

const ThreatCardContent = (
  <div style={{ fontSize: 12.5 }}>
    <p style={{ marginBottom: 8 }}>Each threat card shows:</p>
    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.75, textAlign: 'left' }}>
      <li><strong>STRIDE badge</strong> — threat category (S / T / R / I / D / E)</li>
      <li><strong>Threat ID</strong> — unique reference number</li>
      <li><strong>Statement</strong> — description of the threat</li>
      <li><strong>Severity</strong> — CRITICAL / HIGH / MEDIUM / LOW</li>
      <li><strong>⚡ Likelihood · ⚙ Tech Impact · 📈 Biz Impact · ⚠ Risk</strong></li>
    </ul>
    <p style={{ marginTop: 8, marginBottom: 0, fontSize: 11.5, color: '#777' }}>
      In All Elements mode, clicking a card highlights its DFD component.
    </p>
  </div>
);

const SortChipsContent = (
  <div style={{ fontSize: 12.5 }}>
    <p style={{ marginBottom: 8 }}>Use these chips to sort threats:</p>
    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.75, textAlign: 'left' }}>
      <li><strong>STRIDE</strong> — canonical S→T→R→I→D→E order</li>
      <li><strong>Severity</strong> — Critical → Low</li>
      <li><strong>Risk</strong> — by risk score</li>
      <li><strong>Likelihood / Tech Impact / Biz Impact</strong> — by average score</li>
    </ul>
    <p style={{ marginTop: 8, marginBottom: 0, fontSize: 11.5, color: '#777' }}>
      Click the active chip again to reverse the order (↑ / ↓).
    </p>
  </div>
);

const PanScrollContent = (
  <div style={{ fontSize: 13 }}>
    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.8, textAlign: 'left' }}>
      <li><strong>Scroll wheel</strong> — zoom in / out toward the cursor</li>
      <li><strong>Middle or right mouse drag</strong> — pan the diagram</li>
    </ul>
  </div>
);

const ZoomControlsContent = (
  <div style={{ fontSize: 13 }}>
    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.8, textAlign: 'left' }}>
      <li><strong>Fit button</strong> — resets the view to fit the whole diagram</li>
      <li><strong>Slider</strong> — sets the zoom level precisely</li>
    </ul>
  </div>
);

// ── Steps ────────────────────────────────────────────────────────────────────

function makeSteps(p) {
  const id = name => `#${p}${name}`;
  return [
    {
      target: id('tm-viewer-root'),
      title: 'Welcome to the Threat Model Viewer',
      content: (
        <div style={{ fontSize: 13 }}>
          <p>This viewer shows the <strong>Data Flow Diagram (DFD)</strong> for your assigned target, along with all identified security threats.</p>
          <p style={{ marginBottom: 0 }}>Use it to understand how data moves through the system.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: id('tm-legend-bar'),
      title: 'Legend Bar',
      content: LegendBarContent,
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: id('dfdViewport'),
      title: 'The DFD Canvas',
      content: DfdRegionContent,
      placement: 'right',
      width: 280,
      floatingOptions: { flipOptions: false },
      disableBeacon: true,
    },
    {
      target: id('dfdViewport'),
      title: 'Hover & Click to Explore',
      content: HoverClickContent,
      placement: 'right',
      width: 280,
      floatingOptions: { flipOptions: false },
      disableBeacon: true,
    },
    {
      target: id('tm-sidebar'),
      title: 'Sidebar: Threats & Paths',
      content: SidebarContent,
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: id('tm-first-threat-card'),
      title: 'Reading a Threat Card',
      content: ThreatCardContent,
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: id('tm-sort-chips'),
      title: 'Sorting Threats',
      content: SortChipsContent,
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: id('dfdViewport'),
      title: 'Pan & Zoom',
      content: PanScrollContent,
      placement: 'right',
      width: 280,
      floatingOptions: { flipOptions: false },
      disableBeacon: true,
    },
    {
      target: id('tm-zoom-controls'),
      title: 'Zoom Controls',
      content: ZoomControlsContent,
      placement: 'bottom',
      disableBeacon: true,
    },
  ];
}

// ── Styles ───────────────────────────────────────────────────────────────────

const joyrideOptions = {
  primaryColor: '#5e35b1',
  zIndex: 10000,
  width: 420,
};

const joyrideStyles = {
  tooltip: {
    borderRadius: 8,
    padding: '16px 20px',
  },
  tooltipTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 13,
    padding: 0,
  },
  buttonNext: {
    borderRadius: 6,
    fontSize: 13,
    padding: '6px 16px',
  },
  buttonBack: {
    borderRadius: 6,
    fontSize: 13,
    padding: '6px 14px',
    color: '#5e35b1',
  },
  buttonSkip: {
    fontSize: 12,
    color: '#999',
  },
};

// ── Component ────────────────────────────────────────────────────────────────

export default function TmViewerTourJoyride({ run, onFinish, idPrefix = '' }) {
  const steps = makeSteps(idPrefix);
  const handleCallback = useCallback((data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onFinish();
    }
  }, [onFinish]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep={false}
      disableScrolling
      spotlightPadding={6}
      options={joyrideOptions}
      styles={joyrideStyles}
      locale={{
        back: '← Back',
        close: 'Close',
        last: 'Got it — start exploring',
        next: 'Next →',
        skip: 'Skip tour',
      }}
      callback={handleCallback}
    />
  );
}
